import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import ArticleCard from "@/components/feature/ArticleCard";
import { categories } from "@/mocks/categories";
import { searchArticles } from "@/utils/content";

const hotKeywords = ["巡演", "悬疑剧", "时装周", "Y2K", "Citywalk", "黑胶"];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const urlKeyword = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(urlKeyword);

  useEffect(() => {
    setKeyword(urlKeyword);
  }, [urlKeyword]);

  const results = searchArticles(keyword);
  const hasKeyword = keyword.trim().length > 0;

  const updateKeyword = (value: string) => {
    setKeyword(value);
    if (value.trim()) setParams({ q: value.trim() }, { replace: true });
    else setParams({}, { replace: true });
  };

  return (
    <>
      <Seo
        title={hasKeyword ? `“${keyword}” 的搜索结果 - 噜噜社` : "搜索 - 噜噜社"}
        description="在噜噜社搜索音乐、影视、时尚与潮流文化的相关内容，快速定位你感兴趣的文章与专题。"
        keywords="噜噜社搜索,娱乐资讯搜索,潮流文化搜索"
      />

      <section className="w-full border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[900px] px-4 py-10 md:px-6 md:py-14">
          <h1 className="text-2xl font-extrabold text-foreground-950 md:text-3xl">搜索噜噜社</h1>
          <p className="mt-2 text-sm text-foreground-500">输入关键词即可即时匹配标题、摘要、标签与分类（前端演示搜索）</p>

          <div className="mt-5 flex items-center gap-3 rounded-lg border border-background-300 bg-background-50 px-3 py-2 focus-within:border-primary-400">
            <i className="ri-search-line text-lg text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => updateKeyword(e.target.value)}
              placeholder="例如：巡演、悬疑剧、Y2K…"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            {hasKeyword && (
              <button
                type="button"
                onClick={() => updateKeyword("")}
                aria-label="清空"
                className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-background-200"
              >
                <i className="ri-close-line" />
              </button>
            )}
          </div>

          {!hasKeyword && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">热门搜索</span>
              {hotKeywords.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => updateKeyword(k)}
                  className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-primary-100 hover:text-primary-800"
                >
                  {k}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="mx-auto w-full max-w-[900px] px-4 py-8 md:px-6">
        {hasKeyword ? (
          <>
            <p className="text-sm text-foreground-600">
              关键词 <strong className="text-primary-600">“{keyword.trim()}”</strong> 共找到{" "}
              <strong className="text-primary-600">{results.length}</strong> 条结果
            </p>

            {results.length > 0 ? (
              <div className="mt-5 flex flex-col gap-4">
                {results.map((a) => (
                  <ArticleCard key={a.id} article={a} variant="list" />
                ))}
              </div>
            ) : (
              <div className="mt-5 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-16 text-center">
                <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
                <h2 className="mt-4 text-lg font-semibold text-foreground-900">未找到相关结果</h2>
                <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或从下面的热门分类开始浏览。</p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {categories.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/category/${c.slug}`}
                      className="cursor-pointer rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      {c.name}资讯
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div>
            <h2 className="text-base font-bold text-foreground-950">按分类浏览</h2>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="group flex cursor-pointer items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-2xl text-primary-700">
                    <i className={c.icon} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                      {c.name}
                    </span>
                    <span className="mt-1 block line-clamp-1 text-xs text-foreground-500">{c.tagline}</span>
                  </span>
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}